import { useMemo, useSyncExternalStore } from 'react';

// https://docs.launchdarkly.com/sdk/client-side/react
import { useFlags as _useFlags, type LDClient, type LDContext } from 'launchdarkly-react-client-sdk';
import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';

import { version } from '../../package.json';
import type { RawGeoData } from '../features/geography/types';
import { getErrorMessage } from '../utils/errors';
import { getCookie, setSecureCookie, removeSecureCookie } from '../utils/secureCookies';

import { getBrowserSessionId } from './analytics/getBrowserSessionId';
import type { Profile } from './auth';
import { PlatformEnum } from './platformContext/platformContextTypes';
import type { LDFlags } from './types';

export let ldClient: LDClient | null = null;
const LD_CONTEXT_COOKIE_NAME = 'ldContext';

// Most re-identify paths (the `user/profile` response interceptor, `updateLaunchDarklyUserContext`)
// only have a profile at hand, so they used to rebuild the context without `ip_country` — silently
// dropping an attribute that the bootstrap context in render.tsx does send. Cache the last known
// raw geo here so it survives those re-identifies. Kept in this module rather than read from
// liveGeoStore to avoid an import cycle (LiveGeoStore imports `getRequest` from services/api).
let lastKnownRawGeoData: RawGeoData | null = null;

export function setLastKnownRawGeoData(rawGeoData: RawGeoData | null) {
  lastKnownRawGeoData = rawGeoData;
}

// Server-evaluated flags (`profile.ldBootstrapFlags`), kept as a fallback for when the browser SDK's
// own evaluation is missing or belongs to someone else. player-backend evaluates these with the full
// user-stats context, so they are at least as correct as anything the SDK would compute here.
//
// The SDK only bootstraps at construction: a session that starts logged out boots on the anonymous
// flag set, and the post-login `identify()` is what replaces it. When that identify fails — LD's
// client endpoint has returned 500 for every production web request since 2026-09-14 — the SDK keeps
// the anonymous values, with no way to hand it a new bootstrap. These let us re-seed from the login
// or profile response instead.
//
// Replaced wholesale in setServerFlags and never mutated, so its identity doubles as the
// useSyncExternalStore snapshot.
let serverFlags: Record<string, unknown> | null = null;
let serverFlagsKey: string | null = null;
const serverFlagsListeners = new Set<() => void>();

/**
 * Re-seeds the server-evaluated flags from a freshly fetched profile. Called from
 * `UserStore.setUserProfile`, which every profile in the app passes through.
 */
export function setServerFlags(profile: Profile | null): void {
  const nextKey = profile?.firebaseUid ?? null;
  // `$flagsState`/`$valid` ride along with allFlagsState().toJSON() on the server side and are not
  // flags — drop them here so neither lookups nor the useFlags() spread ever see them.
  const { $flagsState: _flagsState, $valid: _valid, ...rest } = profile?.ldBootstrapFlags ?? {};
  const nextFlags = profile?.ldBootstrapFlags ? rest : null;

  if (serverFlagsKey === nextKey && isEqual(serverFlags, nextFlags)) {
    return;
  }

  serverFlags = nextFlags;
  serverFlagsKey = nextKey;
  serverFlagsListeners.forEach((listener) => listener());
}

function subscribeToServerFlags(listener: () => void): () => void {
  serverFlagsListeners.add(listener);
  return () => serverFlagsListeners.delete(listener);
}

const getServerFlagsSnapshot = () => serverFlags;

/**
 * Whether the SDK's flag set belongs to the user the server last evaluated for.
 *
 * Relies on the SDK advancing its stored context ONLY once a flag fetch has succeeded:
 * `initialize()` sets it up front (so a client constructed with a logged-in bootstrap reports that
 * user immediately), while `identify()` sets it inside the `.then` after `fetchFlagSettings`
 * resolves — so a failed identify leaves the previous, usually anonymous, key in place. Undocumented
 * but long-standing; `launchDarkly.serverFlags.test.ts` pins it.
 */
function sdkHoldsFlagsForCurrentUser(): boolean {
  if (!ldClient || !serverFlagsKey) {
    return false;
  }

  try {
    return ldClient.getContext()?.key === serverFlagsKey;
  } catch {
    return false;
  }
}

export function getServerFlag<T>(flag: LDFlags): T | undefined {
  const value = serverFlags?.[flag];
  return isNil(value) ? undefined : (value as T);
}

/**
 * Resolution order, given whatever value the SDK currently holds for a flag:
 *
 *  1. the SDK's value, when its flag set belongs to the current user — keeps live streaming updates
 *     authoritative whenever LaunchDarkly is healthy;
 *  2. the server-evaluated value, when the SDK's set is for someone else (an anonymous boot whose
 *     post-login identify failed) or it has no value at all (bootstrap missing and the initial fetch
 *     failed);
 *  3. the SDK's value anyway, then the caller's default.
 */
function pickFlag<T>(flag: LDFlags, defaultValue: T, sdkValue: T | undefined): T {
  if (!isNil(sdkValue) && sdkHoldsFlagsForCurrentUser()) {
    return sdkValue;
  }

  const serverValue = getServerFlag<T>(flag);
  if (!isNil(serverValue)) {
    return serverValue;
  }

  return isNil(sdkValue) ? defaultValue : sdkValue;
}

export function getLDContextFromCookie(): LDContext | null {
  try {
    const cookieValue = getCookie(LD_CONTEXT_COOKIE_NAME);

    if (!cookieValue) {
      return null;
    }

    const context = JSON.parse(cookieValue) as LDContext;
    return context;
  } catch (err) {
    const errorMessage = getErrorMessage(err);
    console.error(`Failed to read LD context from cookie: ${errorMessage}`);
    return null;
  }
}

// Keeps only the user-stats fields allowlisted for LaunchDarkly targeting. Spreading the full stats
// blob (100+ fields) bloats the context, which overflows the SDK's flag-settings request and collapses
// it to just `key`; filtering keeps the identified context and the bootstrap cookie small and identical.
function filterStatsByAllowlist(userStats: Record<string, unknown>, allowlist: string[]): Record<string, unknown> {
  const allowed = new Set(allowlist);
  return Object.fromEntries(Object.entries(userStats).filter(([key]) => allowed.has(key)));
}

function computeCookieContent(
  ldContext: LDContext,
  cookieContext: LDContext | null,
  userStats?: Record<string, unknown>,
  frontendAttributesForLD?: string[] | null,
): LDContext | null {
  // Anonymous users: use ldContext as-is
  if (ldContext.anonymous === true) {
    return ldContext;
  }

  // Authenticated users with stats: merge ldContext with filtered userStats by frontendAttributesForLD
  if (userStats && frontendAttributesForLD) {
    return { ...ldContext, ...filterStatsByAllowlist(userStats, frontendAttributesForLD) };
  }

  // If cookie exists, only update existing cookie keys with values from ldContext
  // This prevents large ldContext from overwriting the cookie with unwanted fields
  if (cookieContext) {
    const updatedCookie: LDContext = { ...cookieContext };
    for (const key of Object.keys(cookieContext)) {
      const ldValue = ldContext[key as keyof LDContext];
      if (ldValue !== undefined) {
        (updatedCookie as Record<string, unknown>)[key] = ldValue;
      }
    }
    return updatedCookie;
  }

  // No existing cookie and no userStats: don't save anything
  return null;
}

export function saveLDContextToCookie(
  ldContext: LDContext,
  userStats?: Record<string, unknown>,
  ldAttributes?: string[] | null,
): void {
  try {
    const cookieContext = getLDContextFromCookie();
    const newCookieContent = computeCookieContent(ldContext, cookieContext, userStats, ldAttributes);

    if (!newCookieContent) {
      return;
    }

    if (cookieContext && isEqual(cookieContext, newCookieContent)) {
      return;
    }

    // Prevent cookie size from exceeding 3KB
    const newCookieContentStringify = JSON.stringify(newCookieContent);
    const MAX_COOKIE_SIZE_BYTES = 3 * 1024; // 3KB limit for cookie safety
    if (new Blob([newCookieContentStringify]).size > MAX_COOKIE_SIZE_BYTES) {
      return;
    }

    setSecureCookie(LD_CONTEXT_COOKIE_NAME, newCookieContentStringify, { expires: 1 });
  } catch (err) {
    removeSecureCookie(LD_CONTEXT_COOKIE_NAME);
    const errorMessage = getErrorMessage(err);
    console.error(`Failed to save LD context to cookie: ${errorMessage}`);
  }
}

export function getBootstrapFlags(
  userBootstrapFlags?: Record<string, any> | null,
  anonymousBootstrapFlags?: Record<string, any> | null,
): Record<string, any> | undefined {
  // Backend-evaluated flags from login (authenticated, fresh)
  if (userBootstrapFlags) {
    return userBootstrapFlags;
  }

  // Backend-evaluated flags for anonymous users (fresh)
  if (anonymousBootstrapFlags) {
    return anonymousBootstrapFlags;
  }

  // No bootstrap — SDK will fetch flags directly (3s timeout)
  return undefined;
}

function decodeLdAttributes(ldAttributes: string | null | undefined): string[] | null {
  try {
    if (!ldAttributes) {
      return null;
    }
    return JSON.parse(atob(ldAttributes));
  } catch (err) {
    const errorMessage = getErrorMessage(err);
    console.error(`Failed to decode LD attributes: ${errorMessage}`);
    return null;
  }
}

const onClientReady = () => {
  console.log('LaunchDarkly client is ready');
};

const onClientError = (err: Error) => {
  const errorMessage = err.message || err.toString();
  console.error(`LaunchDarkly error: ${errorMessage}`);
};

const onClientFailed = (err: Error) => {
  const errorMessage = err.message || err.toString();
  console.error(`LaunchDarkly failed: ${errorMessage}`);
};

const onClientChange = (changes: any) => {
  console.log(`LaunchDarkly flags changed after identify: ${JSON.stringify(changes)}`);
};

export function setLDClient(client: LDClient) {
  if (ldClient) {
    ldClient.off('ready', onClientReady);
    ldClient.off('error', onClientError);
    ldClient.off('failed', onClientFailed);
  }

  ldClient = client;

  ldClient.on('ready', onClientReady);
  ldClient.on('error', onClientError);
  ldClient.on('failed', onClientFailed);
  ldClient.on('change', onClientChange);
}

export async function identifyUser(ldContext: LDContext, attempt = 0): Promise<'success' | 'failed'> {
  // Retries, not attempts: `attempt` starts at 0, so this allows 1 initial call plus maxRetries more.
  const maxRetries = 3;

  if (!ldClient) {
    return 'failed';
  }

  try {
    await ldClient.identify(ldContext);
    saveLDContextToCookie(ldContext);
    return 'success';
  } catch (err) {
    const errorMessage = getErrorMessage(err);
    console.error(`LaunchDarkly identify error: ${errorMessage}, attempt ${attempt}`);

    if (attempt >= maxRetries) {
      console.error(`Failed to identify user after ${maxRetries + 1} attempts`);
      return 'failed';
    }

    console.warn(`Retrying identifyUser, attempt ${attempt + 1}`);
    // Awaited, not fire-and-forget: the previous version resolved the caller's promise only on a
    // first-attempt success or a final failure, so any retry left the caller hanging forever — and the
    // caller is a Promise.race in AuthStore, which then always burned its full timeout.
    await new Promise((resolve) => setTimeout(resolve, 1000 * (attempt + 1)));

    return identifyUser(ldContext, attempt + 1);
  }
}

export const getBaseLDContext = (): LDContext => {
  const sessionId = getBrowserSessionId();

  const baseContext: LDContext = {
    kind: 'user',
    session_id: sessionId,
    app_version: version,
    app_platform: PlatformEnum.WEB,
    now: new Date().toISOString(),
  };

  return baseContext;
};

export function getUserStats(stats: Record<string, any> | string | undefined): Record<string, any> | undefined {
  if (typeof stats === 'object') {
    return stats;
  }

  if (typeof stats === 'string') {
    try {
      const userStats = JSON.parse(decodeURIComponent(window.atob(stats)));
      return {
        ...getBaseLDContext(),
        ...userStats,
      };
    } catch (err) {
      const errorMessage = getErrorMessage(err);
      console.error(`Failed to parse user stats: ${errorMessage}`);
    }
  }

  return;
}

export function createLDContext(profile?: Profile | null, rawGeoData?: RawGeoData | null): LDContext {
  const { firebaseUid, email, stats, language, ldAttr: ldAttributesEncoded, territory } = profile ?? {};
  const geoData = rawGeoData ?? lastKnownRawGeoData;

  const baseContext: LDContext = {
    country_code: territory?.countryCode,
    state_code: territory?.code,
    ip_country: geoData?.country,
    phoneCountry: profile?.phoneCountry,
    ...getBaseLDContext(),
    ...(profile && firebaseUid
      ? {
          key: firebaseUid,
          email,
          language,
          user_id: profile.userId,
        }
      : {
          anonymous: true,
          key: 'PLAYER_FRONTEND_ANONYMOUS',
        }),
  };

  const userStats = getUserStats(stats);
  const ldttributes = decodeLdAttributes(ldAttributesEncoded);

  // Send only the allowlisted attributes to the identified context (see filterStatsByAllowlist).
  const allowlistedStats = userStats && ldttributes ? filterStatsByAllowlist(userStats, ldttributes) : userStats;
  const ldContext: LDContext = firebaseUid ? { ...baseContext, ...allowlistedStats } : baseContext;

  saveLDContextToCookie(baseContext, allowlistedStats, ldttributes);

  return ldContext;
}

/**
 * Returns the static value of a flag
 * @param flag
 * @param defaultValue
 */
export function resolveFlag<T = boolean>(flag: LDFlags, defaultValue: T): T {
  if (ldClient) {
    // Ask with no default so an unknown flag is distinguishable from one whose value happens to
    // equal the caller's default — pickFlag needs to know which it is.
    return pickFlag(flag, defaultValue, ldClient.variation(flag, undefined) as T | undefined);
  }

  const serverValue = getServerFlag<T>(flag);
  if (!isNil(serverValue)) {
    return serverValue;
  }

  console.error(':resolveFlag: LaunchDarkly client is missing', { customAttributes: { flag, defaultValue } });

  if (window.newrelic) {
    window.newrelic.log('LaunchDarkly client is missing error', {
      level: 'error',
      customAttributes: {
        flag,
        defaultValue,
      },
    });

    window.newrelic.noticeError(new Error('LaunchDarkly client is missing'), {
      flag,
      defaultValue,
    });
  }

  return defaultValue;
}

/**
 * Returns the observable value of a flag
 * @param flag
 * @param defaultValue
 */
export function useFlag<T = boolean>(flag: LDFlags, defaultValue: T): T {
  const flags = _useFlags<Record<LDFlags, any>>();
  // Re-render when a profile response re-seeds the server flags mid-session — that is the moment an
  // anonymous-booted session learns its real values.
  useSyncExternalStore(subscribeToServerFlags, getServerFlagsSnapshot, getServerFlagsSnapshot);

  return pickFlag(flag, defaultValue, flags[flag] as T | undefined);
}

/**
 * Wrapper for launch darkly useFlags hook
 */
export function useFlags(): Record<LDFlags, any> {
  const flags = _useFlags<Record<LDFlags, any>>();
  const serverValues = useSyncExternalStore(subscribeToServerFlags, getServerFlagsSnapshot, getServerFlagsSnapshot);

  // Memoised so callers that hold on to the object — a deps array, a context value — don't see a new
  // identity every render. `flags` already changes identity whenever the SDK updates.
  return useMemo(
    // Same precedence as useFlag, applied across the whole set.
    () => (!serverValues || sdkHoldsFlagsForCurrentUser() ? flags : { ...flags, ...serverValues }),
    [flags, serverValues],
  );
}
